import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { 
  Users,
  Loader2,
  Search,
  Database,
  Info,
  ShieldAlert
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Pilots() {
  const navigate = useNavigate();
  const [pilots, setPilots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [totalPilots, setTotalPilots] = useState(0);
  const [source, setSource] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);

  const fetchPilots = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch(`${API}/pilots?limit=1000`, {
        credentials: 'include',
        headers
      });
      
      if (response.status === 403) {
        // Access denied - not admin
        setAccessDenied(true);
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        setPilots(data.pilots || []);
        setTotalPilots(data.total || 0);
        setSource(data.source || '');
      }
    } catch (error) {
      console.error('Error fetching pilots:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPilots();
  }, []);

  const filteredPilots = pilots.filter(pilot => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const fullName = `${pilot.first_name || ''} ${pilot.last_name || ''}`.toLowerCase();
    return (
      (pilot.license_number && pilot.license_number.toLowerCase().includes(query)) ||
      fullName.includes(query) ||
      (pilot.email && pilot.email.toLowerCase().includes(query)) ||
      (pilot.category && pilot.category.toLowerCase().includes(query))
    );
  });

  // Access denied view for non-admin users
  if (accessDenied) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Card className="w-full max-w-md bg-card border-red-500/30">
            <CardContent className="p-8 text-center">
              <div className="mx-auto w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mb-4">
                <ShieldAlert className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="font-heading text-xl uppercase mb-2 text-red-400">
                Acceso Restringido
              </h2>
              <p className="text-muted-foreground mb-4">
                Solo los administradores pueden acceder a la base de datos completa de pilotos.
              </p>
              <p className="text-sm text-muted-foreground">
                Los delegados pueden usar la funcionalidad de la aplicación (subir resultados, ver clasificaciones) 
                sin necesidad de acceder a este listado.
              </p>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="pilots-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading font-black text-2xl uppercase flex items-center gap-2">
              <Database className="w-7 h-7 text-primary" />
              Pilotos AECAR
            </h1>
            <p className="text-muted-foreground text-sm">
              Base de datos oficial de pilotos ({totalPilots} pilotos)
            </p>
          </div>
        </div>

        {/* Info Banner */}
        <div className="bg-primary/10 border border-primary/30 rounded-lg p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-primary mt-0.5 shrink-0" />
          <div>
            <p className="text-sm text-primary font-medium">Base de datos de solo lectura</p>
            <p className="text-xs text-muted-foreground mt-1">
              Esta base de datos es gestionada exclusivamente por el administrador de AECAR. 
              Los delegados pueden consultar los datos pero no modificarlos.
              {source === 'admin_database' && ' (Cargada por el administrador)'}
              {source === 'aecar_master_csv' && ' (Base de datos oficial AECAR)'}
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por licencia, nombre, email o categoría..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-black/20 border-white/10"
            data-testid="pilots-search-input"
          />
        </div>

        {/* Pilots Table */}
        <Card className="bg-card/50 border-white/10">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="w-10 h-10 animate-spin text-primary" />
              </div>
            ) : pilots.length === 0 ? (
              <div className="text-center py-20">
                <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-heading text-xl uppercase mb-2">Sin Pilotos</h3>
                <p className="text-muted-foreground mb-6">
                  El administrador aún no ha cargado la base de datos de pilotos.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table className="standings-table">
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="w-28">Licencia</TableHead>
                      <TableHead>Nombre</TableHead>
                      <TableHead className="hidden sm:table-cell">Email</TableHead>
                      <TableHead className="hidden md:table-cell">Categoría</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPilots.map((pilot, idx) => (
                      <TableRow 
                        key={pilot.pilot_id || pilot.license_number || idx}
                        className="border-white/5"
                        data-testid={`pilot-row-${pilot.license_number}`}
                      >
                        <TableCell className="font-mono font-bold text-primary">
                          {pilot.license_number}
                        </TableCell>
                        <TableCell className="font-medium">
                          {pilot.first_name} {pilot.last_name}
                        </TableCell>
                        <TableCell className="hidden sm:table-cell text-muted-foreground text-sm">
                          {pilot.email || '-'}
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-muted-foreground text-sm">
                          {pilot.category || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {filteredPilots.length > 0 && filteredPilots.length !== pilots.length && (
          <p className="text-sm text-muted-foreground text-center">
            Mostrando {filteredPilots.length} de {pilots.length} pilotos
          </p>
        )}
      </div>
    </DashboardLayout>
  );
}
